// Competition (country | league) normalizer.
// Backend sends strings like "Italy | Serie A" or "Switzerland | Switzerland | Super League".
// We dedupe repeated tokens and render "Country • League".

export interface Competition {
  country: string;
  league: string;
  display: string;
}

function clean(s: string | null | undefined): string {
  return (s || "").replace(/\s+/g, " ").trim();
}

export function normalizeCompetition(
  countryRaw: string | null | undefined,
  leagueRaw: string | null | undefined,
): Competition {
  const country = clean(countryRaw);
  // league can itself contain "Country | League"
  const parts = clean(leagueRaw)
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean);

  // Drop tokens that duplicate the country name (case-insensitive).
  const seen = new Set<string>();
  const ordered: string[] = [];
  const withCountry = country ? [country, ...parts] : parts;
  for (const p of withCountry) {
    const key = p.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    ordered.push(p);
  }

  const resolvedCountry = country || ordered[0] || "";
  const league = ordered.filter((p) => p.toLowerCase() !== resolvedCountry.toLowerCase()).join(" · ") || ordered.slice(1).join(" · ");
  const display = [resolvedCountry, league].filter(Boolean).join(" • ");
  return { country: resolvedCountry, league: league || resolvedCountry, display: display || resolvedCountry || "—" };
}

// Two-letter-ish country code helper for flag chips (best-effort, non-authoritative).
export function countryInitials(country: string): string {
  const c = clean(country);
  if (!c) return "··";
  const words = c.split(" ");
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return c.slice(0, 2).toUpperCase();
}
